import { type ComponentProps, type Key, type ReactNode, useCallback, useMemo, useState } from 'react';
import { type Selection } from 'react-aria-components';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { toIntegrations } from '@gonfalon/navigator/navigators';
import type { Manifest } from '@gonfalon/openapi/types';
import { useSlots } from '@gonfalon/react/useSlots';
import { getIntegrationManifestsQuery } from '@gonfalon/rest-api/hooks/integrationManifests';
import {
  Button,
  Collection,
  Dialog,
  DialogTrigger,
  Group,
  Header,
  IconButton,
  Input,
  Link,
  LinkButton,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  ProgressBar,
  SearchField,
  Select,
  SelectValue,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { keyBy } from 'lodash';
import { omit } from 'lodash';

import { DataSourceType } from '../constants';
import { isLDHosted } from '../isLDHosted';
import { useWarehouseExpIntegrations } from '../MetricDataSources/useWarehouseExpIntegrations';
import type { DataSourceListItem, DataSourceVariant } from '../types';

import styles from './DataSourceSelector.module.css';

// Internal type used to represent the minimum data source and related fields
// needed for rendering as an option within the selector dropdown or dialog
type DataSourceAnnotated = Pick<DataSourceListItem, 'key' | 'name'> & {
  _environmentSummary?: DataSourceListItem['_environmentSummary'];
  _manifest?: Pick<Manifest, 'name' | 'icons' | 'warehouseVendor'>;
  integrationKey?: string;
  svgIcon?: ReactNode;
};

// A group of data source options rendered as a single ListBoxSection. `header` is
// the warehouse vendor name; it is omitted for the ungrouped default option.
type DataSourceGroup = {
  id: string;
  header?: string;
  dataSources: DataSourceAnnotated[];
};

// Header used for warehouse data sources whose manifest (and therefore vendor) has
// not resolved yet, or which report no vendor.
const UNKNOWN_VENDOR_HEADER = 'Other data sources';

/** Maps a warehouse integration key to its configure-documentation path. Add entries here when new warehouses are supported. */
const WAREHOUSE_DOC_PATH: Record<string, string> = {
  'snowflake-experimentation': 'home/warehouse-native/snowflake-configure',
  'databricks-experimentation': 'home/warehouse-native/databricks-configure',
  'redshift-experimentation': 'home/warehouse-native/redshift',
  'bigquery-experimentation': 'home/warehouse-native/bigquery',
};

const defaultDataSource: DataSourceAnnotated = {
  key: DataSourceType.LAUNCHDARKLY_HOSTED,
  name: 'LaunchDarkly native',
  svgIcon: <Icon className={styles.osmoIcon} name="osmo" />,
};

const WAREHOUSE_DATA_EXPORT_DOC_PATH = 'integrations/data-export/warehouse';

const VARIANT_COPY: Record<
  DataSourceVariant,
  {
    createLabel: string;
    emptyStateTitle: string;
    emptyStateDescription: string;
    noManifestsDescription: string;
    setupDescription: string;
  }
> = {
  metric: {
    createLabel: 'Create metric data source',
    emptyStateTitle: 'Create metric data source',
    emptyStateDescription: 'No metric data sources found. Create one to connect your warehouse metric data.',
    noManifestsDescription:
      'Metric data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
    setupDescription: 'Set up an integration to create metrics direct from your warehouse data source.',
  },
  assignment: {
    createLabel: 'Create',
    emptyStateTitle: 'Create assignment source',
    emptyStateDescription:
      'No assignment sources found. Create one to use your warehouse assignment data to run an experiment.',
    noManifestsDescription:
      'Assignment data sources can be created from existing data warehouse integrations. Currently, there are no supported data warehouse integrations in your account.',
    setupDescription: 'Set up an integration to analyze experiments direct from your warehouse data source.',
  },
};

const DataSourceEmptyState = ({ title, description }: { title: string; description: string }) => (
  <div className={styles.emptyState} role="status">
    <h3 className={styles.emptyStateTitle}>{title}</h3>
    <p className={styles.emptyStateDescription}>{description}</p>
  </div>
);

const SetupIntegrationFooter = () => (
  <LinkButton href={toIntegrations()} target="_blank" className={styles.createOption}>
    Set up integration
    <Icon name="link-external" size="small" />
  </LinkButton>
);

const DocumentationFooter = () => (
  <LinkButton
    href={getDocumentationUrl(WAREHOUSE_DATA_EXPORT_DOC_PATH)}
    target="_blank"
    className={styles.createOption}
  >
    Learn more about Warehouse data export
    <Icon name="link-external" size="small" />
  </LinkButton>
);

function DataSourcesSearchField({ ...props }: ComponentProps<typeof SearchField>) {
  return (
    <SearchField {...props} aria-label="Search data sources…" autoFocus>
      <Group>
        <Icon name="search" size="small" />
        <Input placeholder="Search data sources…" />
        <IconButton aria-label="clear" icon="cancel-circle-outline" size="small" variant="minimal" />
      </Group>
    </SearchField>
  );
}

const DataSourceEnvironmentSummary = ({
  environmentSummary,
}: {
  environmentSummary: DataSourceListItem['_environmentSummary'];
}) => {
  return (
    <span className={styles.dataSourceEnvironment}>
      <EnvironmentMarker color={environmentSummary?.color} />
      <span className={styles.dataSourceEnvironmentName}>{environmentSummary?.name}</span>
    </span>
  );
};

const DataSourceOption = (dataSourceAnnotated: DataSourceAnnotated) => (
  <ListBoxItem id={dataSourceAnnotated.key} key={dataSourceAnnotated.key}>
    <Text slot="label" className={styles.dataSourceOptionLabel}>
      {dataSourceAnnotated.svgIcon ||
        (dataSourceAnnotated._manifest?.icons?.square ? (
          <img
            src={dataSourceAnnotated._manifest?.icons?.square}
            alt={`${dataSourceAnnotated._manifest?.name} integration logo`}
            className={styles.integrationIcon}
          />
        ) : (
          <ProgressBar aria-label="Integration manifest icon" className={styles.iconPlaceholder} isIndeterminate />
        ))}
      {dataSourceAnnotated.name}
    </Text>
    <Text slot="description" className={styles.dataSourceEnvironment}>
      {!isLDHosted(dataSourceAnnotated.key) && dataSourceAnnotated?._environmentSummary ? (
        <>
          in environment: <DataSourceEnvironmentSummary environmentSummary={dataSourceAnnotated._environmentSummary} />
        </>
      ) : (
        'in any environment.'
      )}
    </Text>
  </ListBoxItem>
);

// Renders a group of data sources as a ListBoxSection, with the warehouse vendor as
// its header. Used as the dynamic render function for the grouped ListBox.
const DataSourceGroupSection = (group: DataSourceGroup) => (
  <ListBoxSection id={group.id}>
    {group.header ? <Header>{group.header}</Header> : null}
    <Collection items={group.dataSources}>{DataSourceOption}</Collection>
  </ListBoxSection>
);

type BaseProps = {
  children?: React.ReactNode;
  dataSourceVariant: DataSourceVariant;
  dataSources: DataSourceListItem[];
  selectionMode?: 'single' | 'multiple';
  variant?: 'dropdown' | 'dialog';
  showDefaultOption?: boolean;
  showSearchField?: boolean;
  /** Render a create-data-source action in the dropdown footer. */
  showCreateOption?: boolean;
  /** Invoked when the create action is pressed. Required for the action to do anything. */
  onCreateOption?: () => void;
  /** Hide the subtitle row (environment + Learn more link) below the trigger. */
  hideSubtitle?: boolean;
};
type SingleModeProps = {
  selectionMode?: 'single';
  selectedKey?: DataSourceListItem['key'];
  onChange?: (dataSource: DataSourceListItem | Pick<typeof defaultDataSource, 'key'>) => void;
  selectProps?: Partial<Omit<ComponentProps<typeof Select>, 'onChange'>>;
};
type MultipleModeProps = {
  selectionMode: 'multiple';
  variant: 'dialog';
  selectedKeys?: Array<DataSourceListItem['key']>;
  defaultSelectedKeys?: Array<DataSourceListItem['key']>;
  onChange?: (dataSource: Array<DataSourceListItem | Pick<typeof defaultDataSource, 'key'>>) => void;
};

type DataSourceSelectorProps = BaseProps & (SingleModeProps | MultipleModeProps);

const isSingleSelectionMode = (props: DataSourceSelectorProps): props is BaseProps & SingleModeProps =>
  props.selectionMode === undefined || props.selectionMode === 'single';
const isMultipleSelectionMode = (props: DataSourceSelectorProps): props is BaseProps & MultipleModeProps =>
  props.selectionMode === 'multiple';
const isDropdownVariant = (
  props: DataSourceSelectorProps,
): props is DataSourceSelectorProps & { variant: 'dropdown' } =>
  props.variant === undefined || props.variant === 'dropdown';

const useManifestsMap = () => {
  const { data: manifests } = useQuery(getIntegrationManifestsQuery());

  const warehouseExpManifests = useMemo(() => {
    if (Array.isArray(manifests?.items)) {
      return manifests.items.filter((manifest) => manifest.otherCapabilities?.includes('warehouseExperimentation'));
    }
  }, [manifests]);

  const manifestsByKey = useMemo(() => {
    return keyBy(warehouseExpManifests, 'key');
  }, [warehouseExpManifests]);

  return manifestsByKey;
};

export const DataSourceSelector = (props: DataSourceSelectorProps) => {
  const { selectionMode, children, dataSources, dataSourceVariant } = props;
  const variantCopy = VARIANT_COPY[dataSourceVariant];
  const manifestsMap = useManifestsMap();
  const shouldResolveEmptyStateAction = props.showCreateOption && dataSources.length === 0;
  const { isLoading, hasIntegrations, hasSupportedManifests } = useWarehouseExpIntegrations({
    disabled: !shouldResolveEmptyStateAction,
  });
  const dataSourcesAnnotated = useMemo(() => {
    return dataSources.map((ds) => ({
      name: ds.name,
      key: ds.key,
      _environmentSummary: ds._environmentSummary,
      _manifest: manifestsMap[ds.integrationKey],
      integrationKey: ds.integrationKey,
    }));
  }, [dataSources, manifestsMap]);
  const [{ trigger }] = useSlots(children, {
    trigger: DataSourceSelectorTriggerSlot,
  });
  const createOption =
    props.showCreateOption && props.onCreateOption ? (
      <Button size="medium" slot={null} className={styles.createOption} onPress={props.onCreateOption}>
        {variantCopy.createLabel}
        <Icon name="link-external" size="small" />
      </Button>
    ) : null;
  const emptyListFooterAction = useMemo(() => {
    if (!shouldResolveEmptyStateAction || isLoading) {
      return null;
    }

    if (!hasSupportedManifests) {
      return <DocumentationFooter />;
    }

    if (!hasIntegrations) {
      return <SetupIntegrationFooter />;
    }

    return createOption;
  }, [shouldResolveEmptyStateAction, isLoading, hasSupportedManifests, hasIntegrations, createOption]);
  const emptyListState = useMemo(() => {
    if (shouldResolveEmptyStateAction && isLoading) {
      return (
        <div className={styles.emptyStateMessage} role="status">
          <ProgressBar isIndeterminate aria-label="Loading integrations" />
        </div>
      );
    }

    if (shouldResolveEmptyStateAction && !hasSupportedManifests) {
      return (
        <DataSourceEmptyState
          title="No supported data warehouse integrations"
          description={variantCopy.noManifestsDescription}
        />
      );
    }

    if (shouldResolveEmptyStateAction && !hasIntegrations) {
      return <DataSourceEmptyState title="No integrations yet" description={variantCopy.setupDescription} />;
    }

    return <DataSourceEmptyState title={variantCopy.emptyStateTitle} description={variantCopy.emptyStateDescription} />;
  }, [shouldResolveEmptyStateAction, isLoading, hasSupportedManifests, hasIntegrations, variantCopy]);
  const isSingleMode = isSingleSelectionMode(props);
  const isMultipleMode = isMultipleSelectionMode(props);
  const showPopoverTrigger = isDropdownVariant(props);
  // In uncontrolled mode with multi-select enabled, manage default selected keys state internally
  // to support "Clear" button functionality
  const [defaultSelectedKeys, setDefaultSelectedKeys] = useState(
    isMultipleMode ? props.defaultSelectedKeys : undefined,
  );
  const [filterQuery, setFilterQuery] = useState('');

  const searchFilter = useCallback(
    (dataSource: Partial<DataSourceListItem>) => {
      if (!filterQuery) {
        return true;
      }

      if (dataSource.name) {
        return dataSource.name.toLowerCase().includes(filterQuery.toLowerCase());
      }
    },
    [filterQuery],
  );

  // In dropdown variant: display the selected data source in the dialog trigger
  const selectedDataSource: DataSourceListItem | null = useMemo(
    () =>
      showPopoverTrigger && isSingleMode
        ? (dataSources.find((ds) => props?.selectedKey === ds.key) ?? (dataSources.length > 0 ? dataSources[0] : null))
        : null,
    [dataSources, showPopoverTrigger, isSingleMode && props.selectedKey],
  );
  const handleSingleSelectionChange = useCallback(
    (dataSourceKey: Key | Selection | (Set<Key> & { currentKey: string })) => {
      if (isSingleMode) {
        // Normalize to support differing callbacks between LP components: Select (string arg) and ListBox (Set arg)
        const normalDsk = Array.from(dataSourceKey instanceof Set ? dataSourceKey : [dataSourceKey])[0];

        if (normalDsk === defaultDataSource.key) {
          // If the selection is the default data source, only return the key
          return props?.onChange?.({ key: defaultDataSource.key });
        }

        const dataSource = dataSources.find((ds) => ds.key === normalDsk);
        if (dataSource) {
          props?.onChange?.(dataSource);
        }
      }
    },
    [dataSources, props.onChange, isSingleMode],
  );
  const handleMultipleSelectionChange = useCallback(
    (dataSourceKeys: Selection) => {
      if (isMultipleMode) {
        if (dataSourceKeys === 'all') {
          return props?.onChange?.(dataSources);
        } else {
          type SelectedDataSource = DataSourceListItem | Pick<typeof defaultDataSource, 'key'>;
          const selectedDataSources = Array.from(dataSourceKeys).flatMap((dsk): SelectedDataSource[] => {
            if (dsk === DataSourceType.LAUNCHDARKLY_HOSTED) {
              return [defaultDataSource];
            }

            const dataSource = dataSources.find((ds) => ds.key === dsk);
            return dataSource ? [dataSource] : [];
          });

          if (Array.isArray(props.defaultSelectedKeys)) {
            setDefaultSelectedKeys(selectedDataSources.map((ds) => ds.key));
          }

          props?.onChange?.(selectedDataSources);
        }
      }
    },
    [dataSources, props.onChange, isMultipleMode],
  );
  // Group the (search-filtered) data sources by warehouse vendor so each vendor
  // renders as its own ListBoxSection. The default option, when shown, is kept in
  // its own headerless group so every ListBox child is a section.
  const groups = useMemo<DataSourceGroup[]>(() => {
    const result: DataSourceGroup[] = [];

    if (props.showDefaultOption && searchFilter(defaultDataSource)) {
      result.push({ id: '__default__', dataSources: [defaultDataSource] });
    }

    const dataSourcesByVendor = new Map<string, DataSourceAnnotated[]>();
    for (const dataSource of dataSourcesAnnotated) {
      if (!searchFilter(dataSource)) {
        continue;
      }
      const vendor = dataSource._manifest?.warehouseVendor ?? UNKNOWN_VENDOR_HEADER;
      const existing = dataSourcesByVendor.get(vendor);
      if (existing) {
        existing.push(dataSource);
      } else {
        dataSourcesByVendor.set(vendor, [dataSource]);
      }
    }

    for (const [vendor, sources] of dataSourcesByVendor) {
      result.push({ id: vendor, header: vendor, dataSources: sources });
    }

    return result;
  }, [props.showDefaultOption, dataSourcesAnnotated, searchFilter]);
  const emptyStateMessage = 'No data sources found.';
  const dropdownTrigger = trigger || (
    <Button className={styles.dataSourceSelectorTrigger}>
      <SelectValue className={styles.selectionValue} />
      <Icon name="chevron-down" size="small" />
    </Button>
  );
  const dropdownPopoverContent = (
    <div className={clsx(styles.popoverContent, dataSources.length === 0 && styles.popoverContentEmpty)}>
      <div className={styles.popoverMain}>
        {props.showSearchField && <DataSourcesSearchField onChange={setFilterQuery} value={filterQuery} />}
        {groups.length === 0 ? (
          dataSources.length === 0 ? (
            emptyListState
          ) : (
            <div className={styles.emptyStateMessage} role="status">
              {emptyStateMessage}
            </div>
          )
        ) : (
          <ListBox
            className={styles.dataSourcesList}
            aria-label="Data sources"
            items={groups}
            renderEmptyState={() => emptyStateMessage}
          >
            {DataSourceGroupSection}
          </ListBox>
        )}
      </div>
      {shouldResolveEmptyStateAction
        ? isLoading
          ? null
          : emptyListFooterAction && <div className={styles.footer}>{emptyListFooterAction}</div>
        : createOption && <div className={styles.footer}>{createOption}</div>}
    </div>
  );
  const dropdownPopoverProps = {
    width: 'fit' as const,
    placement: 'bottom start' as const,
    className: styles.dataSourcePopover,
  };

  if (!showPopoverTrigger) {
    const selections = isMultipleMode
      ? Array.isArray(props.defaultSelectedKeys)
        ? defaultSelectedKeys
        : props.selectedKeys
      : undefined;
    const hasMultipleSelections = isMultipleMode && Array.isArray(selections) && selections.length > 0;

    // Dialog variant: Render the inner dialog content only.
    // Consuming parent is responsible for rendering the dialog and trigger.
    return (
      <>
        {props.showSearchField && <DataSourcesSearchField onChange={setFilterQuery} value={filterQuery} />}
        <ListBox
          className={styles.dataSourcesList}
          aria-label="Data sources"
          selectionMode={selectionMode}
          items={groups}
          renderEmptyState={() => emptyStateMessage}
          {...(isSingleMode
            ? { selectedKey: props.selectedKey, onSelectionChange: handleSingleSelectionChange }
            : isMultipleMode
              ? {
                  selectedKeys: selections,
                  onSelectionChange: handleMultipleSelectionChange,
                }
              : null)}
        >
          {DataSourceGroupSection}
        </ListBox>
        {isMultipleMode && hasMultipleSelections && (
          <div className={styles.clearContainer}>
            <Button
              variant="minimal"
              size="small"
              onPress={() => {
                handleMultipleSelectionChange(new Set());

                if (Array.isArray(props.defaultSelectedKeys)) {
                  setDefaultSelectedKeys([]);
                }
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </>
    );
  }

  if (dataSources.length === 0) {
    // Select does not open its popover with an empty collection, so use DialogTrigger instead.
    return (
      <>
        <Select className={styles.dataSourceSelector} {...omit(props?.selectProps ?? {}, ['onChange'])}>
          <DialogTrigger>
            {dropdownTrigger}
            <Popover {...dropdownPopoverProps}>
              <Dialog>{dropdownPopoverContent}</Dialog>
            </Popover>
          </DialogTrigger>
        </Select>
      </>
    );
  }

  return (
    // Dropdown variant: Render a button trigger which opens a popover when clicked.
    // Consumer not required to render outer dialog or trigger, however it can optionally
    // provide a form field label via children slot.
    <>
      <Select
        className={styles.dataSourceSelector}
        value={selectedDataSource?.key}
        onChange={(key) => {
          if (key) {
            handleSingleSelectionChange(key);
          }
        }}
        {...omit(props?.selectProps ?? {}, ['onChange'])}
      >
        {dropdownTrigger}

        <Popover {...dropdownPopoverProps}>{dropdownPopoverContent}</Popover>
      </Select>
      {showPopoverTrigger && !props.hideSubtitle && selectedDataSource && selectedDataSource.environmentKey && (
        <Text slot="subtitle" className={styles.dataSourceSelectorEnvironment}>
          <span>from </span>
          <DataSourceEnvironmentSummary environmentSummary={selectedDataSource._environmentSummary} />
          <span> environment. </span>
          <Link
            href={getDocumentationUrl(
              WAREHOUSE_DOC_PATH[selectedDataSource.integrationKey ?? ''] ??
                'home/warehouse-native/snowflake-configure',
            )}
            target="_blank"
          >
            Learn more <Icon name="book-external-link" size="tiny" />
          </Link>
        </Text>
      )}
    </>
  );
};

export const DataSourceSelectorTriggerSlot = ({ children }: { children: React.ReactNode }) => children;
